// Destination places: the map's primary layer.
//
// Per the round 3 addendum a pin is a PLACE, not a person and not a single
// entry. Several independent travellers post diaries at the same destination
// and one pin carries all of them.
//
// The server owns this grouping (GET /places?bbox=...). This module holds the
// same grouping rule on the client for two reasons: the map still works
// against an API build that has not shipped /places yet, and a moment that
// arrives over the realtime topic can be folded into a place immediately,
// without a round trip, so a new diary entry becomes a new pin with no
// reload. Both paths use ONE rule, so the two never disagree.
import type { AuthoredMoment, Place, PublicUser } from "./types";

/** Exact match precision from the contract: roughly 6 decimal places. */
const PRECISION = 6;
/** The contract caps the traveller list, while the count stays exact. */
export const PLACE_TRAVELLER_CAP = 8;

export function placeIdFor(lat: number, lng: number): string {
  return `${lat.toFixed(PRECISION)},${lng.toFixed(PRECISION)}`;
}

/** The most common non empty name, ties broken by first seen. */
function commonName(names: Array<string | null>): string | null {
  const counts = new Map<string, number>();
  for (const name of names) {
    const trimmed = name?.trim();
    if (!trimmed) continue;
    counts.set(trimmed, (counts.get(trimmed) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const [name, count] of counts) {
    if (count > bestCount) {
      best = name;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Group public moments into destination places, newest first inside each.
 * `travellerCount` is the number of DISTINCT authors and stays exact even
 * when the avatar list is truncated, because the map renders `+N` from it.
 */
export function groupMomentsIntoPlaces(moments: AuthoredMoment[]): Place[] {
  const buckets = new Map<string, AuthoredMoment[]>();
  for (const moment of moments) {
    if (moment.lat === null || moment.lng === null) continue;
    const id = placeIdFor(moment.lat, moment.lng);
    const bucket = buckets.get(id);
    if (bucket) bucket.push(moment);
    else buckets.set(id, [moment]);
  }

  const places: Place[] = [];
  for (const [id, bucket] of buckets) {
    const ordered = [...bucket].sort((a, b) => b.capturedAt.localeCompare(a.capturedAt));
    const seen = new Set<string>();
    const travellers: PublicUser[] = [];
    for (const moment of ordered) {
      const author = moment.author;
      if (!author || seen.has(author.id)) continue;
      seen.add(author.id);
      if (travellers.length < PLACE_TRAVELLER_CAP) travellers.push(author);
    }
    const first = ordered[0]!;
    places.push({
      id,
      lat: first.lat as number,
      lng: first.lng as number,
      placeName: commonName(ordered.map((moment) => moment.placeName)),
      travellerCount: seen.size,
      momentCount: ordered.length,
      travellers,
      lastMomentAt: first.capturedAt,
    });
  }
  return places.sort((a, b) => b.lastMomentAt.localeCompare(a.lastMomentAt));
}

/** Every moment posted at one place, newest first. */
export function momentsAtPlace(moments: AuthoredMoment[], placeId: string): AuthoredMoment[] {
  return moments
    .filter(
      (moment) =>
        moment.lat !== null &&
        moment.lng !== null &&
        placeIdFor(moment.lat, moment.lng) === placeId,
    )
    .sort((a, b) => b.capturedAt.localeCompare(a.capturedAt));
}

/**
 * The travellers standing behind one marker. A marker can cover several
 * nearby places once they crowd together on screen, so the union is taken by
 * traveller id: the same person at two adjacent destinations is one face and
 * counts once.
 *
 * `total` stays EXACT when every place fully listed its travellers. When a
 * place truncated its list (the contract caps it at 8) the exact
 * `travellerCount` is used instead, so the `+N` chip never under reports.
 */
export function travellersOfPlaces(places: Place[]): { travellers: PublicUser[]; total: number } {
  const seen = new Set<string>();
  const travellers: PublicUser[] = [];
  let truncated = false;
  let sum = 0;
  for (const place of places) {
    sum += place.travellerCount;
    if (place.travellers.length < place.travellerCount) truncated = true;
    for (const traveller of place.travellers) {
      if (seen.has(traveller.id)) continue;
      seen.add(traveller.id);
      travellers.push(traveller);
    }
  }
  return { travellers, total: truncated ? sum : seen.size };
}
